import { createRouter, createWebHistory, RouteRecordRaw, RouteLocationNormalized, NavigationGuardNext } from 'vue-router';
import { IRouteConfigAuthMeta } from '@/shared';
import { main as mainModule, users as userModule } from '@/store/typed/typed-modules';
import { IRouteGroup } from '@/shared/interfaces/route-group';
import { hideFromNavChildClient } from '@/shared/helpers/hide-nav-from-child-client';
import app from '@/main';
import tours from './tours';
import main from './main';
import settings from './client-settings';
import properties from './properties';
import floorplanNavigator from './floorplan-navigator';
import websites, { getBaseSiteAuth } from './websites';
import propertySearch from './property-search';
import unitListBuilder from './unit-list-builder';
import syndication from './syndication';
import leads from './leads';
import reporting from './reporting';
import support from './support';
import auth from './auth';
import dev from './_dev';
import payments from './payments';
import { buildingStackRoutes } from './building-stack';
import iframe from './iframe';
import { IFRAME_NAV_RULES } from './iframe-nav-config';
import i18n from '@/i18n';

// Note: this file is for navbar V1 which we're not using
export const routeGroups: IRouteGroup[] = [
  {
    name: 'Dashboard',
    icon: 'tachometer-fast',
    redirect: '/',
    basePath: '/',
    permissions: [],
    env: [],
    isHidden: () => false,
    routes: main.filter((r) => r.path === '/'),
  },
  {
    name: 'Properties',
    icon: 'house',
    redirect: '/properties',
    basePath: '/properties',
    permissions: ['buildings'],
    env: [],
    isHidden: () => false,
    routes: properties,
  },
  {
    name: 'Websites',
    icon: 'globe',
    redirect: '/websites',
    basePath: '/websites',
    env: [],
    permissions: getBaseSiteAuth(),
    isHidden: () => false,
    routes: websites,
  },
  {
    name: 'Floorplan Navigator',
    icon: 'table-columns',
    redirect: '/floorplan-navigator',
    basePath: '/floorplan-navigator',
    env: [],
    permissions: ['floorplan_navigator'],
    isHidden: () => false,
    routes: floorplanNavigator,
  },
  {
    name: 'Listing Management',
    icon: 'sync-alt',
    redirect: '/syndication',
    basePath: '/syndication',
    permissions: ['standard_listings'],
    env: [],
    isHidden: () => false,
    routes: syndication,
  },
  {
    name: 'Leads',
    icon: 'envelope',
    redirect: '/leads',
    basePath: '/leads',
    permissions: ['lead_tracker'],
    env: [],
    isHidden: () => !!hideFromNavChildClient(),
    routes: leads.filter((l) => l.name !== 'BookingApp'),
  },
  // Alternate leads header nav for child clients that only exposes waitinglists
  {
    name: 'Waiting Lists',
    icon: 'clipboard-list',
    redirect: '/leads/waiting-lists',
    basePath: '/leads',
    permissions: ['waitinglist'],
    env: [],
    isHidden: () => !hideFromNavChildClient(),
    routes: leads.filter((l) => [
      'PropertyWaitingLists',
    ].includes(String(l.name ?? ''))),
  },
  {
    name: 'Tours',
    icon: 'calendar-day',
    redirect: '/tours',
    basePath: '/tours',
    permissions: [],
    env: [],
    isHidden: () => app()?.$rsGlobals.$userHasAtLeastOneAccess('tour_admins')! || app()?.$rsGlobals.$userHasAtLeastOneAccess('tour_agents')!,
    routes: tours,
  },
  {
    name: 'Reports',
    icon: 'chart-bar',
    redirect: '/reporting',
    basePath: '/reporting',
    permissions: [],
    env: [],
    isHidden: () => {
      const vm = app();
      if (!vm) {
        return false;
      }

      const hasGoogleAds = vm.$rsGlobals.$currentUser.getClientSettingValue('google_ads_account_id') !== -1
        && vm.$rsGlobals.$userHasAtLeastOneAccess('digital_marketing_reporting');

      const hasListingScores = !vm.$rsGlobals.$isListingScoresDisabled
        && vm.$rsGlobals.$userHasAtLeastOneAccess('listing_scores_reporting');

      return (
        ![
          'leads_reporting',
          'websites_reporting',
          'tours_reporting',
          'floorplan_navigator_reporting',
        ].find((key) => vm.$rsGlobals.$userHasAtLeastOneAccess(key))
        && !hasGoogleAds && !hasListingScores
      );
    },
    routes: reporting,
  },
  {
    name: 'Dev Tools',
    icon: 'brackets-curly',
    redirect: '/dev',
    basePath: '/dev',
    permissions: ['superuser'],
    env: ['development'],
    isHidden: () => false,
    routes: dev,
  },
];

export const routes: RouteRecordRaw[] = [
  ...main,
  ...properties,
  ...websites,
  ...floorplanNavigator,
  ...syndication,
  ...leads,
  ...tours,
  ...dev,
  ...auth,
  ...payments,
  ...reporting,
  ...settings,
  ...support,
  ...buildingStackRoutes,
  ...propertySearch,
  ...unitListBuilder,
  ...iframe,
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
  await userModule.actions.getCurrent(undefined);
  mainModule.actions.setLoading(false);
  const user = userModule.getters.current();
  const authMeta: IRouteConfigAuthMeta = to.meta?.auth || {};
  const { meta } = to;

  let title: string | undefined;
  if (meta?.titleKey) {
    title = i18n.t(meta.titleKey).toString();
  } else if (meta?.dynamicTitle) {
    title = meta.dynamicTitle();
  } else {
    title = meta?.title;
  }

  document.title = `${title} | Rentsync`;

  if (
    user
    && !user?.isSuperUser
    && window.APP_CONFIG.VUE_APP_MAINTENANCE_MODE === '1'
    && ![from.path, to.path].includes('/maintenance-mode')
  ) {
    next('/maintenance-mode');
    return;
  }

  if (
    window.APP_CONFIG.VUE_APP_MAINTENANCE_MODE !== '1'
    && [from.path, to.path].includes('/maintenance-mode')
  ) {
    next('/');
    return;
  }

  if (!user && to.name !== 'Login' && authMeta.required) {
    next('/login');
    return;
  }

  if (to.name === 'Login' && user) {
    let redirectPath = (to.query.redirect as string) || '/';

    // When in IFrame mode, append the `ext` query param coming from the origin URL to the target URL
    if (window.self !== window.top && to.query.ext) {
      const separator = redirectPath.includes('?') ? '&' : '?';
      redirectPath += `${separator}ext=${encodeURIComponent(String(to.query.ext))}`;
    }

    next(redirectPath);
    return;
  }

  if (user && authMeta.required && authMeta.isDynamicPermitted && !authMeta.isDynamicPermitted()) {
    next('/unauthorized');
    return;
  }

  if (
    user
    && authMeta.required
    && (authMeta.condition === 'or'
      ? !user?.hasAtLeastOneProduct(authMeta?.permissions ?? [])
      : !user?.hasRouterPermission(authMeta?.permissions ?? [], to)
    )
  ) {
    next('/unauthorized');
    return;
  }

  // Injects the iframe nav bar into route meta if the path matches the config
  const iframeNavRule = IFRAME_NAV_RULES.find((r) => r.test.test(to.path));
  if (iframeNavRule && to.meta) {
    to.meta.iframeNav = iframeNavRule.nav;
  }

  next();
});

router.afterEach(() => {
  window.userpilot?.reload();
});

export default router;
